import Controller from '@ember/controller';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { timeout } from 'ember-concurrency';
import type HotelSettingsCompetitorsBenchmarkRoute from 'lumen/routes/hotel/settings/competitors/benchmark';
import {
  BENI_SMART_COMPSET_BUILDER_CHAIN_TRANSLATIONS,
  BENI_SMART_COMPSET_BUILDER_NUMBER_OF_DOTS,
  BENI_SMART_COMPSET_BUILDER_TIMEOUT_DOTS,
} from 'lumen/utils/constants/benchmark-insight/beni-smart-compset-builder-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelSettingsCompetitorsBenchmarkSmartCompsetValidationController extends Controller {
  declare model: ModelFrom<HotelSettingsCompetitorsBenchmarkRoute>;

  @service declare intl: Services['intl'];

  @tracked chainNameIndex = 0;
  @tracked numberOfDots = 0;

  get chainName(): string {
    const chainNameTranslationKey =
      BENI_SMART_COMPSET_BUILDER_CHAIN_TRANSLATIONS[this.chainNameIndex];

    if (!chainNameTranslationKey) {
      return '';
    }

    return this.intl.t(chainNameTranslationKey);
  }

  get dots(): string {
    return '.'.repeat(this.numberOfDots);
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // Reset the indices in case the user steps through the flow multiple times in a row.
    // We want the animation to start from the beginning each time.
    this.chainNameIndex = 0;
    this.numberOfDots = 0;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.incrementIndices();
  }

  async incrementIndices(): Promise<void> {
    // Calling this method multiple times in a row instead of in a loop,
    // to avoid no-await-in-loop linting error.
    await this.incrementNumberOfDots();
    await this.incrementNumberOfDots();
    await this.incrementNumberOfDots();

    if (
      this.chainNameIndex <
      BENI_SMART_COMPSET_BUILDER_CHAIN_TRANSLATIONS.length - 1
    ) {
      this.chainNameIndex += 1;
    } else {
      this.chainNameIndex = 0;
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.incrementIndices();
  }

  async incrementNumberOfDots(): Promise<void> {
    await timeout(BENI_SMART_COMPSET_BUILDER_TIMEOUT_DOTS);

    if (this.numberOfDots < BENI_SMART_COMPSET_BUILDER_NUMBER_OF_DOTS - 1) {
      this.numberOfDots += 1;
    } else {
      this.numberOfDots = 0;
    }
  }
}
